import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, GripVertical, Loader2 } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AdminListShell } from "@/components/admin/AdminListShell";
import { deleteProject, reorderProjects } from "@/lib/projects.functions";
import { adminProjectsQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";

type ProjectRow = {
  id: string;
  slug: string;
  title: string;
  tag_en: string;
  image_url: string | null;
  featured: boolean;
  published: boolean;
  sort_order: number;
};

export const Route = createFileRoute("/admin/projects/")({
  component: ProjectsList,
});

type ProjectsListData = { projects: ProjectRow[] };

const LIST_KEY = INVALIDATE.adminProjects;

/** Server reads order by `featured DESC, sort_order ASC`. Mirror that locally. */
function sortLikeServer(rows: ProjectRow[]): ProjectRow[] {
  return [...rows].sort((a, b) => {
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    return a.sort_order - b.sort_order;
  });
}

function ProjectsList() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const query = useQuery(adminProjectsQuery());
  const rows = (query.data?.projects ?? []) as ProjectRow[];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const invalidateAll = [INVALIDATE.adminProjects, INVALIDATE.publicProjects];

  const deleteMutation = useAuthedMutation(deleteProject, {
    invalidate: invalidateAll,
    invalidateOn: "settled",
    onMutate: async ({ id }: { id: string }) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      const previous = queryClient.getQueryData<ProjectsListData>(LIST_KEY);
      if (previous) {
        queryClient.setQueryData<ProjectsListData>(LIST_KEY, {
          ...previous,
          projects: previous.projects.filter((p) => p.id !== id),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      const ctx = context as { previous?: ProjectsListData } | undefined;
      if (ctx?.previous) queryClient.setQueryData(LIST_KEY, ctx.previous);
    },
    onSuccess: () => router.invalidate(),
  });

  const reorderMutation = useAuthedMutation(reorderProjects, {
    invalidate: invalidateAll,
    invalidateOn: "settled",
    onMutate: async ({ items }: { items: { id: string; sort_order: number }[] }) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      const previous = queryClient.getQueryData<ProjectsListData>(LIST_KEY);
      if (previous) {
        const orderById = new Map(items.map((i) => [i.id, i.sort_order]));
        const next = previous.projects.map((p) =>
          orderById.has(p.id) ? { ...p, sort_order: orderById.get(p.id)! } : p,
        );
        queryClient.setQueryData<ProjectsListData>(LIST_KEY, {
          ...previous,
          projects: sortLikeServer(next),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      const ctx = context as { previous?: ProjectsListData } | undefined;
      if (ctx?.previous) queryClient.setQueryData(LIST_KEY, ctx.previous);
    },
    onSuccess: () => router.invalidate(),
  });

  const onDelete = (id: string) => {
    if (!confirm("Delete this project? This cannot be undone.")) return;
    deleteMutation.mutate({ id });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(rows, oldIndex, newIndex).map((r, i) => ({
      ...r,
      sort_order: i,
    }));
    reorderMutation.mutate({
      items: reordered.map((r) => ({ id: r.id, sort_order: r.sort_order })),
    });
  };

  const errorMessage = deleteMutation.error?.message ?? reorderMutation.error?.message ?? null;
  const saving = reorderMutation.isPending;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            Projects
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Drag the handle on the left of each row to reorder. Lower position appears first.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saving && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving order…
            </span>
          )}
          <Link
            to="/admin/projects/new"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> New project
          </Link>
        </div>
      </div>

      {errorMessage && (
        <p className="mt-6 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {errorMessage}
        </p>
      )}

      <div className="mt-8 overflow-hidden rounded-2xl border border-border">
        <div className="grid grid-cols-[40px_80px_1fr_140px_100px_180px] items-center gap-4 bg-card/50 px-4 py-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">
          <div></div>
          <div>Image</div>
          <div>Title</div>
          <div className="hidden md:block">Tag</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        <AdminListShell
          query={query}
          isEmpty={(d) => (d.projects ?? []).length === 0}
          emptyText="No projects yet. Create your first one."
        >
          {() => (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                {rows.map((r) => (
                  <SortableRow key={r.id} row={r} onDelete={onDelete} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </AdminListShell>
      </div>
    </div>
  );
}

function SortableRow({ row, onDelete }: { row: ProjectRow; onDelete: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-[40px_80px_1fr_140px_100px_180px] items-center gap-4 border-t border-border bg-background px-4 py-3 ${
        isDragging ? "shadow-lg" : ""
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        aria-label="Drag to reorder"
        className="flex h-8 w-8 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-card hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div>
        {row.image_url ? (
          <img
            src={row.image_url}
            alt=""
            width={64}
            height={40}
            className="h-10 w-16 rounded object-cover"
          />
        ) : (
          <div className="h-10 w-16 rounded bg-card" />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-foreground">{row.title}</span>
          {row.featured && (
            <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-foreground">
              Featured
            </span>
          )}
        </div>
        <div className="truncate text-xs text-muted-foreground">{row.slug}</div>
      </div>
      <div className="hidden truncate text-sm text-muted-foreground md:block">{row.tag_en}</div>
      <div>
        <span
          className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-wider ${
            row.published
              ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {row.published ? "Published" : "Draft"}
        </span>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Link
          to="/admin/projects/$id"
          params={{ id: row.id }}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-foreground hover:bg-card"
        >
          <Pencil className="h-3 w-3" /> Edit
        </Link>
        <button
          onClick={() => onDelete(row.id)}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-destructive hover:text-destructive"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>
    </div>
  );
}
